import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { SquaresPlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../../utils/formatting';

// --- Reusable Form Components ---
const FormInput = ({ id, label, type, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type={type} id={id} name={id} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);
const TextArea = ({ id, label, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <textarea id={id} name={id} value={value} onChange={onChange} required={required} rows="4" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);
const FileInput = ({ id, label, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type="file" id={id} name="image" onChange={onChange} required={required} accept="image/png, image/jpeg, image/webp" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-semibold file:bg-orange-50 dark:file:bg-orange-900/50 file:text-orange-700 dark:file:text-orange-200 hover:file:bg-orange-100 dark:hover:file:bg-orange-900" />
    </div>
);

// --- Secure S3 Upload Helper ---
const uploadFileToS3 = async (file, token) => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://givetechtotheblind.com/api';
  console.log('Uploading file with API_URL:', API_URL);
  console.log('File details:', { name: file.name, type: file.type, size: file.size });

  try {
    // CHANGE THIS LINE: Use the new products endpoint instead of onboarding
    const getUrlResponse = await fetch(`${API_URL}/products/generate-presigned-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ fileName: file.name, fileType: file.type })
    });
    
    console.log('Presigned URL response status:', getUrlResponse.status);
    
    if (!getUrlResponse.ok) {
      const errorText = await getUrlResponse.text();
      console.error('Failed to get presigned URL. Status:', getUrlResponse.status, 'Response:', errorText);
      throw new Error(`Server error: ${getUrlResponse.status} - Could not get upload URL`);
    }
    
    const responseData = await getUrlResponse.json();
    console.log('Full presigned URL response:', responseData);
    
    // Handle the response structure
    let presignedUrl, key;
    
    if (responseData.presignedUrl && responseData.key) {
      presignedUrl = responseData.presignedUrl;
      key = responseData.key;
    } else if (responseData.url && responseData.key) {
      presignedUrl = responseData.url;
      key = responseData.key;
    } else if (responseData.urls && responseData.urls.length > 0 && responseData.urls[0].url) {
      presignedUrl = responseData.urls[0].url;
      key = responseData.urls[0].key;
    } else if (responseData.data && responseData.data.presignedUrl) {
      presignedUrl = responseData.data.presignedUrl;
      key = responseData.data.key;
    } else {
      console.error('Unknown response structure:', responseData);
      throw new Error('Server returned an unexpected response format');
    }
    
    if (!presignedUrl) {
      console.error('Presigned URL is undefined in response:', responseData);
      throw new Error('Server did not provide a valid upload URL');
    }
    
    console.log('Got presigned URL:', presignedUrl);
    console.log('Got key:', key);
    
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    
    if (!uploadResponse.ok) {
      console.error('S3 upload failed. Status:', uploadResponse.status);
      throw new Error('Failed to upload product image to S3.');
    }
    
    console.log('S3 upload successful');
    return key;
  } catch (error) {
    console.error('Upload file error:', error);
    throw error;
  }
};
// --- Edit Product Modal Component ---
const EditProductModal = ({ product, onClose, onSave, isSubmitting }) => {
    const [formData, setFormData] = useState({
        ...product,
        image: null // For new image file
    });
    const [imagePreview, setImagePreview] = useState(product.image_url);
    
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        
        if (name === 'image' && files && files[0]) {
            const file = files[0];
            setFormData(prev => ({...prev, image: file}));
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            setFormData(prev => ({...prev, [name]: value}));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    // Clean up object URL when component unmounts
    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Product</h2>
                    
                    {/* Current Image Preview */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Current Image
                        </label>
                        <img 
                            src={imagePreview} 
                            alt={formData.name} 
                            className="w-32 h-32 object-cover rounded-md border border-gray-300 dark:border-gray-600"
                        />
                    </div>
                    
                    <FormInput id="name" name="name" label="Product Name" type="text" value={formData.name} onChange={handleChange} />
                    <FormInput id="price" name="price" label="Price (₦)" type="number" value={formData.price} onChange={handleChange} />
                    <TextArea id="description" name="description" label="Description" value={formData.description} onChange={handleChange} />
                    
                    {/* New Image Upload */}
                    <FileInput 
                        id="editImage" 
                        name="image" 
                        label="Update Product Image (leave empty to keep current)" 
                        onChange={handleChange} 
                        required={false} 
                    />
                    
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} disabled={isSubmitting} className="py-2 px-4 bg-gray-200 dark:bg-slate-600 font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="py-2 px-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600">
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Main Page Component ---
export default function ManageProductsPage() {
    const { token } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ name: '', description: '', price: '', image: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [editingProduct, setEditingProduct] = useState(null);

    // Get API URL with fallback
    const API_URL = process.env.REACT_APP_API_URL || 'https://www.givetechtotheblind.com/api';
    
    const fetchProducts = useCallback(async () => {
        if (!token) {
            console.log('No token available');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/products`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error('Could not fetch products.');
            }
            
            const data = await response.json();
            setProducts(data);
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    }, [token, API_URL]);

    useEffect(() => { 
        fetchProducts(); 
    }, [fetchProducts]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({ ...prev, [name]: files ? files[0] : value }));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        if (!formData.image) {
            setMessage({ type: 'error', text: 'Please select a product image to upload.' });
            setIsSubmitting(false);
            return;
        }
        
        try {
            console.log('Starting file upload...');
            const imageKey = await uploadFileToS3(formData.image, token);
            console.log('File uploaded with key:', imageKey);
            
            const productData = { 
                name: formData.name, 
                description: formData.description, 
                price: formData.price, 
                imageKey 
            };
            
            console.log('Creating product with data:', productData);
            
            const response = await fetch(`${API_URL}/products`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(productData)
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Failed to add product.');
            
            setMessage({ type: 'success', text: `Successfully added "${data.name}"!` });
            setFormData({ name: '', description: '', price: '', image: null });
            e.target.reset();
            await fetchProducts();
        } catch (err) {
            console.error('Add product error:', err);
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Are you sure you want to delete this product? This also deletes its image from storage and cannot be undone.')) return;

        try {
            const response = await fetch(`${API_URL}/products/${productId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setMessage({ type: 'success', text: 'Product deleted successfully!' });
            fetchProducts();
        } catch (err) {
            setMessage({ type: 'error', text: `Error: ${err.message}` });
        }
    };

    const handleEditSave = async (updatedProduct) => {
        setIsSubmitting(true);

        try {
            let imageKey = null;
            
            // If a new image was selected, upload it first
            if (updatedProduct.image) {
                console.log('Uploading new image for product update...');
                imageKey = await uploadFileToS3(updatedProduct.image, token);
                console.log('New image uploaded with key:', imageKey);
            }
            
            // Prepare the update data
            const updateData = {
                name: updatedProduct.name,
                description: updatedProduct.description,
                price: updatedProduct.price,
            };
            
            // Only include imageKey if we successfully uploaded a new image
            if (imageKey) {
                updateData.imageKey = imageKey;
            }
            
            console.log('Updating product with data:', updateData);
            
            const response = await fetch(`${API_URL}/products/${updatedProduct.id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(updateData)
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            
            setMessage({ type: 'success', text: 'Product updated successfully!' });
            setEditingProduct(null);
            fetchProducts();
        } catch (err) {
            console.error('Edit product error:', err);
            setMessage({ type: 'error', text: `Error: ${err.message}` });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <p className="p-8">Loading products...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage Products</h1>
            
            {message.text && (
                <p className={`text-center font-semibold p-3 rounded-md ${
                    message.type === 'success' ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-200' : 
                    'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200'
                }`}>
                    {message.text}
                </p>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <form onSubmit={handleAddSubmit} className="space-y-6">
                        <div className="flex items-center gap-3">
                            <SquaresPlusIcon className="w-8 h-8 text-orange-500" />
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Add New Product</h2>
                        </div>
                        <FormInput id="name" name="name" label="Product Name" type="text" value={formData.name} onChange={handleChange} />
                        <FormInput id="price" name="price" label="Price ($)" type="number" value={formData.price} onChange={handleChange} />
                        <TextArea id="description" name="description" label="Description" value={formData.description} onChange={handleChange} />
                        <FileInput id="image" name="image" label="Product Image" onChange={handleChange} />
                        <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50">
                            {isSubmitting ? 'Adding Product...' : 'Add Product to Catalog'}
                        </button>
                    </form>
                </div>

                <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Existing Products ({products.length})</h2>
                    <div className="space-y-2">
                        {products.length > 0 ? products.map(product => (
                            <div key={product.id} className="flex justify-between items-center p-3 border-b dark:border-gray-600 last:border-b-0">
                                <div className="flex items-center gap-4">
                                    <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded-md" />
                                    <div>
                                        <p className="font-bold text-gray-800 dark:text-white">{product.name}</p>
                                        <p className="text-sm text-gray-500">{formatCurrency(product.price)}</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setEditingProduct(product)} className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full" title="Edit"><PencilIcon className="w-5 h-5"/></button>
                                    <button onClick={() => handleDelete(product.id)} className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full" title="Delete"><TrashIcon className="w-5 h-5"/></button>
                                </div>
                            </div>
                        )) : <p className="text-center text-gray-500 py-8">No products have been added yet.</p>}
                    </div>
                </div>
            </div>
            {editingProduct && (
                <EditProductModal 
                    product={editingProduct}
                    onClose={() => setEditingProduct(null)}
                    onSave={handleEditSave}
                    isSubmitting={isSubmitting}
                />
            )}
        </div>
    );
}